import { ImageFile } from '@adornis/file-utils/db/images.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { calculateMaxPointsFromCriteriaMutation } from '../../../../../../db/api/calculate-max-points.js';
import { getFileByIDQuery } from '../../../../../../db/api/get-all-files.js'; // Pfad ggf. anpassen
import { GRADING_THRESHOLDS } from '../../../../../../shared/constants.js';
import { GRAMMAR_TASK_TYPES } from '../../../../../../shared/grammar-grading.js';
import {
  formatTaskReferenceForDisplay,
  type LanguageUseBoxSource,
} from '../../../../../../shared/grammar-rules-resolution.js';
import type { BoxMode } from '../../grading/components/box-mode-toggle.js';
import { getLanguageUseConfidenceLevelSignal, type ConfidenceLevel, type SaveTarget } from '../../grading/utils.js';
import type { SolutionGradingCheckProps, Task } from '../utils.js';
import {
  buildTaskGroups,
  extractPointsMatches,
  getGroupLandingTaskIndex,
  getVisibleGroupTaskIndices,
  hasZeroContentPoints,
  parseDescriptionBoxesJson,
  scaleCriteriaPoints,
  snapToHalfStep,
} from '../utils.js';
import { useSolutionDom } from './use-solution-dom.js';

interface LanguageUseGroupInfo {
  _id: string;
  criteria: string;
  maxPoints: number;
  confidence?: number;
  isApproved?: boolean;
  dependsOn: string[];
  /** Teacher/chat-facing "Gruppe N" label — null on a rule written before this field existed. */
  displayNumber: number | null;
}

function parseLanguageUseGroupEntry(parsed: any): LanguageUseGroupInfo | null {
  if (!parsed || typeof parsed !== 'object' || !parsed._id) return null;
  return {
    _id: String(parsed._id),
    criteria: typeof parsed.criteria === 'string' ? parsed.criteria : '',
    maxPoints: Number(parsed.maxPoints) || 0,
    confidence: typeof parsed.confidence === 'number' ? parsed.confidence : undefined,
    isApproved: parsed.isApproved === true,
    dependsOn: Array.isArray(parsed.dependsOn) ? parsed.dependsOn.map(String) : [],
    displayNumber: typeof parsed.displayNumber === 'number' ? parsed.displayNumber : null,
  };
}

/** Every LanguageUseScheme group a task belongs to — a task can belong to more than one. */
function parseLanguageUseGroupsJson(raw: string | null | undefined): LanguageUseGroupInfo[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(parseLanguageUseGroupEntry).filter((group): group is LanguageUseGroupInfo => !!group);
  } catch {
    return [];
  }
}

/** Same rule as hasZeroContentPoints, for a task's shared LanguageUseScheme group(s). */
function hasZeroGroupLanguageUsePoints(group: LanguageUseGroupInfo | null): boolean {
  return !!group && group.criteria.trim().length > 0 && group.maxPoints <= 0;
}

function scalarToStatus(confidence: number | undefined): 'low' | 'medium' | 'ok' {
  if (confidence === undefined || !Number.isFinite(confidence)) return 'ok';
  if (confidence < GRADING_THRESHOLDS.LOW_CONFIDENCE) return 'low';
  if (confidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE) return 'medium';
  return 'ok';
}

export function useSolutionState(props: SolutionGradingCheckProps) {
  const {
    examId,
    tasks,
    pages = [],
    currentTaskIndex,
    onTaskChange,
    onUpdateMasterSolution,
    onUpdateGradingScheme,
    onApproveMasterSolution,
    onApproveGradingScheme,
    onDeleteSolutionImage,
    onAddSolutionImage,
    onUpdateLanguageUseGroupMembership,
    onDeleteLanguageUseGroup,
    isGradingInProgress = false,
    refiningMasterSolutionTaskIds,
    refiningGradingSchemeTaskIds,
    refiningGradingSchemeLanguageUseTaskIds,
  } = props;
  const dom = useSolutionDom();

  // --- Core State ---
  const [isEditingMasterSolution, setIsEditingMasterSolution] = useState(false);
  const [isEditingGradingScheme, setIsEditingGradingScheme] = useState(false);
  const [masterSolutionDraft, setMasterSolutionDraft] = useState('');
  const [gradingCriteriaDraft, setGradingCriteriaDraft] = useState('');
  const [gradingMaxPointsDraft, setGradingMaxPointsDraft] = useState<number>(0);
  const [isCalculatingMaxPoints, setIsCalculatingMaxPoints] = useState(false);
  const calcTokenRef = useRef(0);
  const lastNonZeroMaxPointsRef = useRef(0);
  // Per-box content/language-use toggle — reset whenever the current task changes (see effect below).
  const [boxMode, setBoxMode] = useState<BoxMode>('content');
  const [activeLanguageUseSourceKey, setActiveLanguageUseSourceKey] = useState<string | null>(null);
  const [languageUseCriteriaDraft, setLanguageUseCriteriaDraft] = useState('');
  const [languageUseMaxPointsDraft, setLanguageUseMaxPointsDraft] = useState<number>(0);
  // Draft membership (task ids) for the currently open group's picker popover — null while closed.
  const [groupMembershipDraft, setGroupMembershipDraft] = useState<Set<string> | null>(null);
  const [isSavingGroupMembership, setIsSavingGroupMembership] = useState(false);
  const [groupMembershipError, setGroupMembershipError] = useState<string | null>(null);
  // Transient message shown next to the delete-rule button on failure (e.g. a race lost against the
  // isGradingInProgress check) — auto-clears so it reads as a flash, not a persistent banner.
  const [deleteLanguageUseGroupError, setDeleteLanguageUseGroupError] = useState<string | null>(null);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Each entry pairs a resolved URL with its source imageId so the two never drift apart when
  // some ids fail to resolve (see resolution effect below).
  const [resolvedImages, setResolvedImages] = useState<Array<{ imageId: string; url: string }>>([]);
  const [resolvedMasterSolutionImages, setResolvedMasterSolutionImages] = useState<
    Array<{ imageId: string; url: string }>
  >([]);
  const [resolvedGradingSchemeImages, setResolvedGradingSchemeImages] = useState<
    Array<{ imageId: string; url: string }>
  >([]);
  // Which section, if any, is currently uploading dropped images (drives the dropzone spinner).
  const [uploadingImageType, setUploadingImageType] = useState<'masterSolution' | 'gradingScheme' | null>(null);

  // --- Computed Context ---
  const actualIndex = currentTaskIndex >= tasks.length ? 0 : currentTaskIndex;
  const currentTask = tasks[actualIndex] || tasks[0];
  const currentTaskKey = String(currentTask?.id ?? currentTask?.taskNumber ?? actualIndex);
  const imageIdsKey = Array.isArray(currentTask?.imageIds) ? currentTask.imageIds.join('|') : '';
  const masterSolutionImageIdsKey = Array.isArray(currentTask?.masterSolutionImageIds)
    ? currentTask.masterSolutionImageIds.join('|')
    : '';
  const gradingSchemeImageIdsKey = Array.isArray(currentTask?.gradingSchemeImageIds)
    ? currentTask.gradingSchemeImageIds.join('|')
    : '';
  const languageUseGroupsJsonKey = currentTask?.languageUseGroupsJson ?? '';
  const descriptionBoxesJsonKey = currentTask?.descriptionBoxesJson ?? '';

  const currentTaskDescriptionBoxes = useMemo(
    () => parseDescriptionBoxesJson(descriptionBoxesJsonKey || null),
    [descriptionBoxesJsonKey],
  );

  const { taskGroups } = buildTaskGroups(tasks);
  const currentTaskNum = currentTask
    ? (currentTask.taskNumber ?? currentTask.name.replace(/^Aufgabe\s+/i, '').trim())
    : '';
  const currentDotIdx = currentTaskNum.indexOf('.');
  const currentMainNum = currentDotIdx === -1 ? currentTaskNum : currentTaskNum.slice(0, currentDotIdx);
  const isMainTaskWithoutDirectAnswer = currentDotIdx === -1 && currentTask?.needsAnswer === false;

  const currentGroupIdx = taskGroups.findIndex(g => g.mainNum === currentMainNum);
  const currentGroup = taskGroups[currentGroupIdx] ?? null;
  // Any task whose content came from an extraction carries confidence data worth showing. Per task now, so a
  // generated solution sitting next to an uploaded one no longer switches the colours off for both. A task
  // whose grading scheme (content or language-use, own or shared group) already has content but
  // zero/negative own points is included too, regardless of source — it blocks grading start (see
  // ownMaxPoints/evaluateGradingSchemeComplete) independently of any confidence signal.
  const showConfidenceColors = tasks.some(
    task =>
      task.masterSolutionSource === 'upload' ||
      task.gradingSchemeSource === 'upload' ||
      hasZeroContentPoints(task) ||
      parseLanguageUseGroupsJson(task.languageUseGroupsJson).some(hasZeroGroupLanguageUsePoints),
  );

  // --- Language-use box-mode sources: every LanguageUseScheme group surfaced by
  // MasterSolutionDTO.languageUseGroupsJson for the current task (solo 1-member or multi-member —
  // every language-use rule is a LanguageUseScheme now). A task can belong to more than one;
  // activeLanguageUseSourceKey (below) picks which one is shown/edited. ---
  const currentLanguageUseGroups = useMemo(
    () => parseLanguageUseGroupsJson(languageUseGroupsJsonKey || null),
    [languageUseGroupsJsonKey],
  );
  const languageUseBoxSources: LanguageUseBoxSource[] = useMemo(
    () =>
      currentLanguageUseGroups.map(group => ({
        kind: 'group' as const,
        key: group._id,
        dependsOn: group.dependsOn,
        displayNumber: group.displayNumber,
      })),
    [currentLanguageUseGroups],
  );
  const activeLanguageUseSource: LanguageUseBoxSource | null =
    languageUseBoxSources.find(source => source.key === activeLanguageUseSourceKey) ?? languageUseBoxSources[0] ?? null;
  const activeLanguageUseGroup: LanguageUseGroupInfo | null =
    currentLanguageUseGroups.find(group => group._id === activeLanguageUseSource?.key) ?? currentLanguageUseGroups[0] ?? null;

  // Every task selectable in the group-membership picker: structural (TASK_FRAME) and non-answerable
  // tasks can't hold a language-use rule so they're excluded entirely; language-use-eligible tasks are
  // listed first, then the rest (still manually addable — see plan).
  const groupMembershipPickerTasks: Task[] = useMemo(() => {
    const selectable = tasks.filter(task => !!task.id && task.taskType !== 'TASK_FRAME' && task.needsAnswer !== false);
    const eligible = selectable.filter(task => !!task.taskType && GRAMMAR_TASK_TYPES.has(task.taskType));
    const rest = selectable.filter(task => !(task.taskType && GRAMMAR_TASK_TYPES.has(task.taskType)));
    return [...eligible, ...rest];
  }, [tasks]);

  const currentLanguageUseMaxScore: number = Math.max(0, activeLanguageUseGroup?.maxPoints ?? 0);

  // Worst signal across EVERY group the current task belongs to (not just the active/selected one) —
  // this drives the BoxModeToggle's language-use dot, which must warn about an issue even in a group
  // the chevrons aren't currently pointing at.
  const languageUseConfidenceDotLevel = useMemo(() => {
    // Zero points takes priority — same rule as groupLanguageUseByTaskIndex, checked here too since
    // this dot is a separate computation (drives the BoxModeToggle's language-use dot, not the
    // dropdown/panel maps).
    if (currentLanguageUseGroups.some(hasZeroGroupLanguageUsePoints)) return 'low';
    return getLanguageUseConfidenceLevelSignal(
      currentLanguageUseGroups.map(group => ({ confidence: group.confidence, isApproved: group.isApproved, schemeId: group._id })),
    );
  }, [currentLanguageUseGroups]);

  // Rule editing (criteria/points/membership) always targets the LanguageUseScheme now, regardless of
  // member count — see grammar-rules-resolution.ts module doc for why grading mechanics still differ
  // by member count even though the rule itself is unified.
  const currentSaveTarget: SaveTarget =
    boxMode !== 'languageUse' ? { kind: 'content' } : { kind: 'group', languageUseSchemeId: activeLanguageUseSource?.key ?? '' };

  // Task-scoped, not per-student — MS/GS are single artifacts shared by the whole class, so these just
  // answer "is THIS task's box currently being (re)generated", for whoever has the canvas open.
  //
  // Job-sourced (see get-live-solution-jobs.ts / chat.tsx's live-solution-jobs effect): the sets already
  // hold the exact gradable-node ids a live job targets, so the current task's OWN id is checked directly
  // — a call is addressed to a whole root subtree, but rarely targets every node in it.
  const isMasterSolutionRefining = !!currentTask?.id && !!refiningMasterSolutionTaskIds?.has(currentTask.id);
  const isGradingSchemeContentRefining = !!currentTask?.id && !!refiningGradingSchemeTaskIds?.has(currentTask.id);
  // A languageUse rule can span several main tasks (a multi-member group) — it's "refining" if ANY of the
  // tasks it depends on is currently being (re)generated or (re)refined for languageUse specifically —
  // refiningGradingSchemeLanguageUseTaskIds is scoped to that dimension alone, so a content-only refine
  // never lights this up (and vice versa).
  const isGradingSchemeLanguageUseRefining = !!activeLanguageUseGroup?.dependsOn.some(dep => {
    const depTask = tasks.find(t => t.taskNumber === formatTaskReferenceForDisplay(dep));
    return !!depTask?.id && !!refiningGradingSchemeLanguageUseTaskIds?.has(depTask.id);
  });

  // Confidence is an OCR/extraction signal — generation and refine never produce one (their LLM call has
  // no such field, see buildGradingSchemePrompts/buildMasterSolutionPrompts), so a task whose content came
  // from either still carries whatever confidence its PRIOR state happened to have (a refine spreads
  // `...existing` and never touches `confidence`). Gated the same way `shouldColorConfidence` below already
  // gates the panel's border/color — on `source === 'upload'` — so a generated/refined task can't keep
  // showing the "prüfen" review button off a stale number that no longer describes anything real.
  const masterSolutionConfidenceStatusMap = useMemo(() => {
    const map: Record<number, 'low' | 'medium' | 'ok'> = {};
    tasks.forEach((task, index) => {
      map[index] =
        task.masterSolutionSource !== 'upload'
          ? 'ok'
          : task.masterSolutionIsApproved
            ? 'ok'
            : scalarToStatus(task.masterSolutionConfidence);
    });
    return map;
  }, [tasks]);

  const gradingSchemeConfidenceStatusMap = useMemo(() => {
    const map: Record<number, 'low' | 'medium' | 'ok'> = {};
    tasks.forEach((task, index) => {
      // Zero own points is checked BEFORE source/isApproved and takes priority — it blocks grading start
      // independently of any confidence signal, regardless of where the content came from.
      map[index] = hasZeroContentPoints(task)
        ? 'low'
        : task.gradingSchemeSource !== 'upload'
          ? 'ok'
          : task.gradingSchemeIsApproved
            ? 'ok'
            : scalarToStatus(task.gradingSchemeConfidence);
    });
    return map;
  }, [tasks]);

  // Per-task language-use rule confidence, folded across EVERY group the task belongs to (worst wins,
  // via getLanguageUseConfidenceLevelSignal) since a task can belong to more than one now. Kept
  // alongside all the groups' own ids — a rule linked to several tasks (dependsOn) is one underlying
  // decision, so uncertaintyTaskIndices below dedupes by these ids instead of counting it once per
  // linked task (a solo rule's id is unique to its one task, so this naturally behaves like "one count
  // per task" for solo rules too).
  const groupLanguageUseByTaskIndex = useMemo(() => {
    const map: Record<number, { status: 'low' | 'medium' | 'ok'; groupIds: string[] }> = {};
    tasks.forEach((task, index) => {
      const groups = parseLanguageUseGroupsJson(task?.languageUseGroupsJson);
      if (groups.length === 0) {
        map[index] = { status: 'ok', groupIds: [] };
        return;
      }
      const groupIds = groups.map(group => group._id);
      if (groups.some(hasZeroGroupLanguageUsePoints)) {
        map[index] = { status: 'low', groupIds };
        return;
      }
      const signal = getLanguageUseConfidenceLevelSignal(
        groups.map(group => ({ confidence: group.confidence, isApproved: group.isApproved, schemeId: group._id })),
      );
      map[index] = { status: signal === 'none' ? 'ok' : signal, groupIds };
    });
    return map;
  }, [tasks]);

  // Per-task language-use confidence, same 'low'|'medium'|'ok' vocabulary as the content maps above —
  // feeds the dropdown's secondary language-use dot.
  const languageUseConfidenceStatusMap = useMemo(() => {
    const map: Record<number, 'low' | 'medium' | 'ok'> = {};
    tasks.forEach((_task, index) => {
      map[index] = groupLanguageUseByTaskIndex[index]?.status ?? 'ok';
    });
    return map;
  }, [tasks, groupLanguageUseByTaskIndex]);

  // Dropdown row coloring: worst of content (master solution + grading scheme) AND language-use, so a
  // task whose only open issue is language-use-side still shows up red/amber here instead of plain
  // text — reuses the three per-dimension maps above rather than re-deriving raw confidence values.
  const confidenceStatusMap = useMemo(() => {
    const map: Record<number, 'low' | 'medium' | 'ok'> = {};
    tasks.forEach((_task, index) => {
      const statuses = [
        masterSolutionConfidenceStatusMap[index],
        gradingSchemeConfidenceStatusMap[index],
        languageUseConfidenceStatusMap[index],
      ];
      map[index] = statuses.includes('low') ? 'low' : statuses.includes('medium') ? 'medium' : 'ok';
    });
    return map;
  }, [tasks, masterSolutionConfidenceStatusMap, gradingSchemeConfidenceStatusMap, languageUseConfidenceStatusMap]);

  // Content counterpart to languageUseConfidenceDotLevel — same dot treatment on the box-mode
  // toggle's "Inhalt" pill, since the panel's border color alone was easy to miss next to the
  // language-use dot.
  const contentConfidenceDotLevel: ConfidenceLevel = useMemo(() => {
    const status = gradingSchemeConfidenceStatusMap[actualIndex];
    return status === 'ok' || status === undefined ? 'none' : status;
  }, [gradingSchemeConfidenceStatusMap, actualIndex]);

  // A shared language-use-group's uncertainty is one underlying decision even though it's linked to
  // several tasks — dedupe it by groupId so it only contributes one task index (both for navigation
  // and for the displayed count, which is this array's length) instead of one per linked task. A task
  // can now carry several groupIds; it counts as a new uncertainty if AT LEAST ONE of them hasn't been
  // seen yet, and all of them are marked seen either way.
  const uncertaintyTaskIndices = useMemo(() => {
    if (!showConfidenceColors) return [];

    const seenGroupIds = new Set<string>();
    return tasks.flatMap((_task, index) => {
      const hasContentUncertainty = [
        masterSolutionConfidenceStatusMap[index],
        gradingSchemeConfidenceStatusMap[index],
      ].some(status => status === 'low' || status === 'medium');

      const groupInfo = groupLanguageUseByTaskIndex[index];
      const groupUncertain = groupInfo?.status === 'low' || groupInfo?.status === 'medium';
      const groupIds = groupInfo?.groupIds ?? [];
      const isNewGroup = groupUncertain && groupIds.some(id => !seenGroupIds.has(id));
      groupIds.forEach(id => seenGroupIds.add(id));

      return hasContentUncertainty || isNewGroup ? [index] : [];
    });
  }, [gradingSchemeConfidenceStatusMap, masterSolutionConfidenceStatusMap, groupLanguageUseByTaskIndex, showConfidenceColors, tasks]);

  useEffect(() => {
    if (gradingMaxPointsDraft > 0) lastNonZeroMaxPointsRef.current = gradingMaxPointsDraft;
  }, [gradingMaxPointsDraft]);

  // --- Effects ---
  useEffect(() => {
    setIsEditingMasterSolution(false);
    setIsEditingGradingScheme(false);
    setCurrentImageIndex(0);
    setResolvedImages([]);
    setResolvedMasterSolutionImages([]);
    setResolvedGradingSchemeImages([]);
    setBoxMode('content');
    setActiveLanguageUseSourceKey(null);
    if (currentTask) {
      setMasterSolutionDraft(currentTask.masterSolution);
      setGradingCriteriaDraft(currentTask.gradingCriteria || '');
      setGradingMaxPointsDraft(currentTask.gradingMaxPoints ?? 0);
    }
  }, [currentTaskKey]);

  // Keep the active language-use source valid: default to the first source when entering
  // language-use mode or when the current task's sources change (e.g. after navigating tasks).
  useEffect(() => {
    if (boxMode !== 'languageUse' || languageUseBoxSources.length === 0) return;
    if (languageUseBoxSources.some(source => source.key === activeLanguageUseSourceKey)) return;
    setActiveLanguageUseSourceKey(languageUseBoxSources[0]?.key ?? null);
  }, [boxMode, languageUseBoxSources, activeLanguageUseSourceKey]);

  // A task's last language-use source can disappear without any navigation (e.g. it was the sole
  // member of a group and got removed from it via the membership picker) — BoxModeToggle hides its
  // whole chip row once sources.length is 0, so without this the panel is stuck showing an empty
  // language-use box with no visible way back to "Inhalt" short of navigating away and back.
  useEffect(() => {
    if (boxMode === 'languageUse' && languageUseBoxSources.length === 0) setBoxMode('content');
  }, [boxMode, languageUseBoxSources]);

  // Sync the language-use edit drafts to the active (chevron-selected) group's rule — also re-syncs
  // when the user switches which group is active, not just when the task changes.
  useEffect(() => {
    setLanguageUseCriteriaDraft(activeLanguageUseGroup?.criteria ?? '');
    setLanguageUseMaxPointsDraft(activeLanguageUseGroup?.maxPoints ?? 0);
  }, [currentTaskKey, activeLanguageUseGroup]);

  useEffect(() => {
    const resolveImageIds = async (ids: string[] | undefined): Promise<Array<{ imageId: string; url: string }>> => {
      if (!Array.isArray(ids) || ids.length === 0) return [];
      const pairs = await Promise.all(
        ids.map(async id => {
          const url = await (getFileByIDQuery as any)(id)({ _id: 1, meta: { fileName: 1, extension: 1 } })
            .then((file: any) => (file ? file.getServeLink() : null))
            .catch(() => null);
          return url ? { imageId: id, url: url as string } : null;
        }),
      );
      // Drop unresolved entries as imageId/url units so the two arrays stay index-aligned.
      return pairs.filter(Boolean) as Array<{ imageId: string; url: string }>;
    };

    let cancelled = false;
    void Promise.all([
      resolveImageIds(currentTask?.imageIds),
      resolveImageIds(currentTask?.masterSolutionImageIds),
      resolveImageIds(currentTask?.gradingSchemeImageIds),
    ]).then(([descriptionImages, msImages, gsImages]) => {
      if (cancelled) return;
      setResolvedImages(descriptionImages);
      setResolvedMasterSolutionImages(msImages);
      setResolvedGradingSchemeImages(gsImages);
    });

    return () => {
      cancelled = true;
    };
  }, [currentTaskKey, imageIdsKey, masterSolutionImageIdsKey, gradingSchemeImageIdsKey]);

  useEffect(() => {
    if (!isMainTaskWithoutDirectAnswer || !currentGroup) return;
    const target = currentGroup.subTasks[0]?.flatIndex;
    if (target !== undefined && target !== null && target !== actualIndex) {
      onTaskChange(target);
    }
  }, [isMainTaskWithoutDirectAnswer, currentGroup, actualIndex, onTaskChange]);

  useEffect(() => {
    const visibleIndices = getVisibleGroupTaskIndices(currentGroup);
    if (visibleIndices.length === 0 || visibleIndices.includes(actualIndex)) return;

    const target = getGroupLandingTaskIndex(currentGroup) ?? visibleIndices[0];
    if (target !== undefined && target !== null && target !== actualIndex) {
      onTaskChange(target);
    }
  }, [actualIndex, currentGroup, onTaskChange]);

  // --- Handlers ---
  const handleApproveMasterSolution = () => {
    onApproveMasterSolution?.(actualIndex);
  };

  // Accepts an explicit target so a caller can approve content or language-use independently of which
  // box mode is currently selected — approving must act on whichever section is actually flagged, not
  // on whatever the user happens to be looking at (see GradingSchemePanel's split review buttons).
  const handleApproveGradingScheme = (target?: SaveTarget) => {
    onApproveGradingScheme?.(actualIndex, target ?? currentSaveTarget);
  };

  const handleDeleteSolutionImage = (type: 'masterSolution' | 'gradingScheme', imageId: string) => {
    if (!imageId) return;
    // Optimistically drop the image from the resolved list so it disappears immediately,
    // then let the parent persist the deletion (mutation + cache update).
    const setter = type === 'masterSolution' ? setResolvedMasterSolutionImages : setResolvedGradingSchemeImages;
    setter(prev => prev.filter(img => img.imageId !== imageId));
    onDeleteSolutionImage?.(actualIndex, imageId, type);
  };

  const handleAddSolutionImages = async (type: 'masterSolution' | 'gradingScheme', files: File[]) => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    setUploadingImageType(type);
    try {
      for (const file of imageFiles) {
        try {
          const imageId = await ImageFile.upload(file);
          if (!imageId) continue;

          // Resolve the serve link so the image shows immediately; the parent persists the box.
          const url = await (getFileByIDQuery as any)(imageId)({ _id: 1, meta: { fileName: 1, extension: 1 } })
            .then((f: any) => (f ? f.getServeLink() : null))
            .catch(() => null);

          if (url) {
            const setter = type === 'masterSolution' ? setResolvedMasterSolutionImages : setResolvedGradingSchemeImages;
            setter(prev => [...prev, { imageId, url }]);
          }

          onAddSolutionImage?.(actualIndex, imageId, type);
        } catch (error) {
          console.error('[useSolutionState] image upload failed', error);
        }
      }
    } finally {
      setUploadingImageType(null);
    }
  };

  const handleSaveMasterSolution = () => {
    if (currentTask) {
      // onUpdateMasterSolution's own mutation chain already approves server-side after saving
      // (see chat.tsx) — a separate onApproveMasterSolution call here raced it with its own
      // independent read-modify-write of the whole tasks array, occasionally reverting the just-saved
      // content when its stale read landed after the content write committed.
      onUpdateMasterSolution(actualIndex, masterSolutionDraft);
      setIsEditingMasterSolution(false);
    }
  };

  const handleCancelMasterSolution = () => {
    if (currentTask) {
      setMasterSolutionDraft(currentTask.masterSolution);
      setIsEditingMasterSolution(false);
    }
  };

  const handleSaveGradingScheme = () => {
    if (!currentTask) return;
    if (boxMode === 'languageUse') {
      calcTokenRef.current++;
      onUpdateGradingScheme(
        actualIndex,
        {
          criteria: languageUseCriteriaDraft,
          maxPoints: Number.isFinite(languageUseMaxPointsDraft) ? languageUseMaxPointsDraft : 0,
        },
        currentSaveTarget,
      );
    } else {
      calcTokenRef.current++;
      onUpdateGradingScheme(
        actualIndex,
        {
          criteria: gradingCriteriaDraft,
          maxPoints: Number.isFinite(gradingMaxPointsDraft) ? gradingMaxPointsDraft : 0,
        },
        { kind: 'content' },
      );
    }
    setIsEditingGradingScheme(false);
  };

  const handleCancelGradingScheme = () => {
    if (!currentTask) return;
    calcTokenRef.current++;
    setGradingCriteriaDraft(currentTask.gradingCriteria || '');
    setGradingMaxPointsDraft(currentTask.gradingMaxPoints ?? 0);
    setLanguageUseCriteriaDraft(activeLanguageUseGroup?.criteria ?? '');
    setLanguageUseMaxPointsDraft(activeLanguageUseGroup?.maxPoints ?? 0);
    setIsEditingGradingScheme(false);
  };

  const handleLanguageUseCriteriaChange = (nextCriteria: string) => {
    setLanguageUseCriteriaDraft(nextCriteria);
    const pointsMatches = extractPointsMatches(nextCriteria);
    const sum = pointsMatches.reduce((acc, item) => acc + item.value, 0);
    const snappedSum = snapToHalfStep(sum);

    // Same rule as handleGradingCriteriaChange: skip the LLM call when the regex sum already
    // matches, invalidate any in-flight call otherwise.
    if (snappedSum === languageUseMaxPointsDraft) {
      calcTokenRef.current++;
      setIsCalculatingMaxPoints(false);
      return;
    }

    if (nextCriteria.trim().length === 0) {
      calcTokenRef.current++;
      setIsCalculatingMaxPoints(false);
      setLanguageUseMaxPointsDraft(0);
      return;
    }

    const token = ++calcTokenRef.current;
    setIsCalculatingMaxPoints(true);
    void calculateMaxPointsFromCriteriaMutation(nextCriteria, currentTask?.masterSolution ?? '', examId)()
      .then(result => {
        if (calcTokenRef.current !== token) return;
        setLanguageUseMaxPointsDraft(Math.max(0, snapToHalfStep(result)));
      })
      .catch(() => {
        /* leave maxPoints unchanged on error */
      })
      .finally(() => {
        if (calcTokenRef.current === token) setIsCalculatingMaxPoints(false);
      });
  };

  const handleLanguageUseMaxPointsChange = (rawValue: string) => {
    const numericValue = Number(String(rawValue).replace(',', '.')) || 0;
    setLanguageUseMaxPointsDraft(Math.max(0, snapToHalfStep(numericValue)));
  };

  // --- Language-use-group membership picker (split/merge an existing LanguageUseScheme group) ---
  // All of these act on activeLanguageUseGroup — the group the chevrons currently point at — not
  // just "the" group, since a task can belong to more than one.
  const openGroupMembershipEditor = () => {
    if (!activeLanguageUseGroup) return;
    // Match by comparing the group's "|"-joined qualified paths (formatted to dotted display style)
    // against each task's own taskNumber — the same helper the read-only badge already uses.
    const matchedIds = tasks
      .filter(task => !!task.taskNumber && activeLanguageUseGroup.dependsOn.some(dep => formatTaskReferenceForDisplay(dep) === task.taskNumber))
      .map(task => task.id)
      .filter((id): id is string => !!id);
    setGroupMembershipDraft(new Set(matchedIds));
    setGroupMembershipError(null);
    dom.actions.setIsGroupPickerOpen(true);
  };

  const toggleGroupMembershipDraft = (taskId: string) => {
    setGroupMembershipDraft(prev => {
      const next = new Set(prev ?? []);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const cancelGroupMembership = () => {
    setGroupMembershipDraft(null);
    setGroupMembershipError(null);
    dom.actions.setIsGroupPickerOpen(false);
  };

  const saveGroupMembership = () => {
    if (!activeLanguageUseGroup || !groupMembershipDraft || groupMembershipDraft.size === 0) return;
    if (!onUpdateLanguageUseGroupMembership) return;
    setIsSavingGroupMembership(true);
    setGroupMembershipError(null);
    onUpdateLanguageUseGroupMembership(activeLanguageUseGroup._id, Array.from(groupMembershipDraft))
      .then(() => {
        setGroupMembershipDraft(null);
        dom.actions.setIsGroupPickerOpen(false);
      })
      .catch(() => {
        setGroupMembershipError('save-failed');
      })
      .finally(() => {
        setIsSavingGroupMembership(false);
      });
  };

  const deleteLanguageUseGroup = () => {
    if (!activeLanguageUseGroup || isGradingInProgress) return;
    if (!onDeleteLanguageUseGroup) return;
    setDeleteLanguageUseGroupError(null);
    onDeleteLanguageUseGroup(activeLanguageUseGroup._id).catch((error: unknown) => {
      const message = error instanceof Error && error.message ? error.message : 'save-failed';
      setDeleteLanguageUseGroupError(message);
      window.setTimeout(() => setDeleteLanguageUseGroupError(null), 4000);
    });
  };

  const handleGradingMaxPointsChange = (rawValue: string) => {
    const numericValue = Number(String(rawValue).replace(',', '.')) || 0;
    const nextMaxPoints = Math.max(0, snapToHalfStep(numericValue));
    const referenceMaxPoints = lastNonZeroMaxPointsRef.current;
    if (nextMaxPoints > 0 && referenceMaxPoints > 0 && nextMaxPoints !== referenceMaxPoints) {
      const sumFromMatches = extractPointsMatches(gradingCriteriaDraft).reduce((sum, item) => sum + item.value, 0);
      const hadNoMismatch = Math.abs(sumFromMatches - referenceMaxPoints) <= 0.5;
      if (hadNoMismatch) {
        const factor = nextMaxPoints / referenceMaxPoints;
        setGradingCriteriaDraft(scaleCriteriaPoints(gradingCriteriaDraft, factor, nextMaxPoints, true));
      }
    }
    setGradingMaxPointsDraft(nextMaxPoints);
  };

  const handleGradingCriteriaChange = (nextCriteria: string) => {
    setGradingCriteriaDraft(nextCriteria);
    const pointsMatches = extractPointsMatches(nextCriteria);
    const sum = pointsMatches.reduce((acc, item) => acc + item.value, 0);
    const snappedSum = snapToHalfStep(sum);

    // If the captured numeric sum already matches the current maxPoints draft,
    // criteria and maxPoints are consistent — no recalculation needed.
    // Invalidate any in-flight LLM call so its stale result doesn't overwrite this.
    if (snappedSum === gradingMaxPointsDraft) {
      calcTokenRef.current++;
      setIsCalculatingMaxPoints(false);
      return;
    }

    // Empty criteria — clear maxPoints to 0 directly, no need to ask the LLM.
    if (nextCriteria.trim().length === 0) {
      calcTokenRef.current++;
      setIsCalculatingMaxPoints(false);
      setGradingMaxPointsDraft(0);
      return;
    }

    // Sum disagrees with maxPoints — ask the LLM regardless of which synonyms
    // (Punkt, Pkt, P, BE, Bewertungseinheit) appear in the text. The LLM uses
    // the master solution to resolve natural-language counts like "je einen Punkt".
    const token = ++calcTokenRef.current;
    setIsCalculatingMaxPoints(true);
    void calculateMaxPointsFromCriteriaMutation(nextCriteria, currentTask?.masterSolution ?? '', examId)()
      .then(result => {
        if (calcTokenRef.current !== token) return;
        setGradingMaxPointsDraft(Math.max(0, snapToHalfStep(result)));
      })
      .catch(() => {
        /* leave maxPoints unchanged on error */
      })
      .finally(() => {
        if (calcTokenRef.current === token) setIsCalculatingMaxPoints(false);
      });
  };

  const goToPrevMainTask = () => {
    const prev = taskGroups[currentGroupIdx - 1];
    if (!prev) return;
    const target = getGroupLandingTaskIndex(prev);
    if (target !== undefined && target !== null) onTaskChange(target);
  };

  const goToNextMainTask = () => {
    const next = taskGroups[currentGroupIdx + 1];
    if (!next) return;
    const target = getGroupLandingTaskIndex(next);
    if (target !== undefined && target !== null) onTaskChange(target);
  };

  const goToNextUncertainty = () => {
    if (uncertaintyTaskIndices.length === 0) return;

    const currentUncertaintyIndex = uncertaintyTaskIndices.indexOf(actualIndex);
    const nextUncertaintyIndex =
      currentUncertaintyIndex === -1
        ? uncertaintyTaskIndices[0]
        : uncertaintyTaskIndices[(currentUncertaintyIndex + 1) % uncertaintyTaskIndices.length];

    if (nextUncertaintyIndex !== undefined && nextUncertaintyIndex !== null) {
      onTaskChange(nextUncertaintyIndex);
    }
  };

  return {
    refs: dom.refs,
    state: {
      ...dom.state,
      examId,
      isEditingMasterSolution,
      isEditingGradingScheme,
      masterSolutionDraft,
      gradingCriteriaDraft,
      gradingMaxPointsDraft,
      isCalculatingMaxPoints,
      currentImageIndex,
      resolvedImageUrls: resolvedImages.map(img => img.url),
      descriptionImageIds: resolvedImages.map(img => img.imageId),
      resolvedMasterSolutionImageUrls: resolvedMasterSolutionImages.map(img => img.url),
      masterSolutionImageIds: resolvedMasterSolutionImages.map(img => img.imageId),
      resolvedGradingSchemeImageUrls: resolvedGradingSchemeImages.map(img => img.url),
      gradingSchemeImageIds: resolvedGradingSchemeImages.map(img => img.imageId),
      uploadingImageType,
      confidenceStatusMap,
      masterSolutionConfidenceStatusMap,
      gradingSchemeConfidenceStatusMap,
      languageUseConfidenceStatusMap,
      showConfidenceColors,
      uncertaintyTaskIndices,
      boxMode,
      activeLanguageUseSourceKey,
      languageUseCriteriaDraft,
      languageUseMaxPointsDraft,
      groupMembershipDraft,
      isSavingGroupMembership,
      groupMembershipError,
      deleteLanguageUseGroupError,
    },
    computed: {
      tasks,
      pages,
      currentTaskDescriptionBoxes,
      currentTask,
      actualIndex,
      currentDotIdx,
      taskGroups,
      currentGroup,
      currentGroupIdx,
      isMainTaskWithoutDirectAnswer,
      languageUseBoxSources,
      activeLanguageUseSource,
      currentLanguageUseGroups,
      activeLanguageUseGroup,
      currentLanguageUseMaxScore,
      languageUseConfidenceDotLevel,
      contentConfidenceDotLevel,
      groupMembershipPickerTasks,
      isGradingInProgress,
      isMasterSolutionRefining,
      isGradingSchemeContentRefining,
      isGradingSchemeLanguageUseRefining,
    },
    actions: {
      ...dom.actions,
      setIsEditingMasterSolution,
      setIsEditingGradingScheme,
      setMasterSolutionDraft,
      setCurrentImageIndex,
      handleSaveMasterSolution,
      handleCancelMasterSolution,
      handleSaveGradingScheme,
      handleCancelGradingScheme,
      handleApproveMasterSolution,
      handleApproveGradingScheme,
      handleDeleteSolutionImage,
      handleAddSolutionImages,
      handleGradingMaxPointsChange,
      handleGradingCriteriaChange,
      handleLanguageUseMaxPointsChange,
      handleLanguageUseCriteriaChange,
      goToPrevMainTask,
      goToNextMainTask,
      goToNextUncertainty,
      onTaskChange,
      setBoxMode,
      setActiveLanguageUseSourceKey,
      openGroupMembershipEditor,
      toggleGroupMembershipDraft,
      saveGroupMembership,
      cancelGroupMembership,
      deleteLanguageUseGroup,
    },
  };
}

export type SolutionStateStore = ReturnType<typeof useSolutionState>;
